import { FormSection } from "@/components/persons/person-form/FormSection";
import type { PersonForm } from "@/components/persons/person-form/types";
import { WorkingHoursFields } from "@/components/persons/person-form/WorkingHoursFields";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLabelWithHint,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";
import { useRoleClassificationOptions } from "@/hooks/use-role-classifications";
import { useWorkLocationOptions } from "@/hooks/use-work-locations";
import { PERSON_STATUS_LABELS, PERSON_STATUSES } from "@/schemas/persons";

const STATUS_OPTIONS = PERSON_STATUSES.map((value) => ({
  value,
  label: PERSON_STATUS_LABELS[value],
}));

// Sentinels for the "no value" Select rows (Radix Select disallows "" values).
const LOCATION_NONE = "__none__";
const ROLE_CLASSIFICATION_NONE = "__none__";

/**
 * Append the person's currently-held value to a picker's options when the
 * admin-managed list no longer offers it (retired, or deleted outright).
 * Without this, opening the form on such a person shows an empty select and a
 * save silently clears the field. Null/empty is left alone — Radix forbids
 * both `undefined` and `""` as a SelectItem value.
 */
function withHeldValue(
  options: { value: string; label: string }[],
  held: string | null | undefined,
  labelFor: (key: string) => string,
): { value: string; label: string }[] {
  if (!held || options.some((o) => o.value === held)) return options;
  return [...options, { value: held, label: labelFor(held) }];
}

export function EmploymentSection({
  form,
  hasSecondShift,
}: {
  form: PersonForm;
  hasSecondShift: boolean;
}) {
  // Entity name only applies to contractors (B2B billing entity); the value is
  // kept server-side if the type changes, the field just hides. This branches
  // on the code-stable `key`, never the label — renaming "Contractor" to
  // "1099" in Settings must not turn the field off.
  const employmentType = form.watch("employmentType");
  const isContractor = employmentType === "contractor";

  // All three pickers below are admin-managed lists, shown active-only plus
  // this person's current value if it has since been retired — so editing
  // someone doesn't silently blank a field they legitimately hold.
  const employmentTypes = useEmploymentTypeOptions();
  const classifications = useRoleClassificationOptions();
  const locations = useWorkLocationOptions();

  const employmentTypeOptions = withHeldValue(
    employmentTypes.options,
    employmentType,
    employmentTypes.labelFor,
  );
  const roleClassificationOptions = withHeldValue(
    classifications.options,
    form.watch("roleClassification"),
    classifications.labelFor,
  );
  const locationOptions = withHeldValue(
    locations.options,
    form.watch("location"),
    locations.labelFor,
  );

  return (
    <FormSection title="Employment">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job title</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Senior Engineer" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="employmentType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Employment Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {employmentTypeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="roleClassification"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role Classification</FormLabel>
              <Select
                value={field.value ?? ROLE_CLASSIFICATION_NONE}
                onValueChange={(v) => field.onChange(v === ROLE_CLASSIFICATION_NONE ? null : v)}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Role Classification" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={ROLE_CLASSIFICATION_NONE}>
                    <span className="text-muted-foreground">Unspecified</span>
                  </SelectItem>
                  {roleClassificationOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <Select
                value={field.value ?? LOCATION_NONE}
                onValueChange={(v) => field.onChange(v === LOCATION_NONE ? null : v)}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={LOCATION_NONE}>
                    <span className="text-muted-foreground">Unspecified</span>
                  </SelectItem>
                  {locationOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {isContractor && (
          <FormField
            control={form.control}
            name="entityName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Entity name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="B2B entity, e.g. Acme Consulting LLC"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="hoursPerWeek"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Hours per week</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  max={80}
                  step={1}
                  placeholder="40"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(e.target.value === "" ? null : e.target.valueAsNumber)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start date</FormLabel>
              <FormControl>
                <Input type="date" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="rehireDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rehire date</FormLabel>
              <FormControl>
                <Input type="date" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="employeeProfileId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Employee Profile</FormLabel>
              <EntityCombobox
                entity="employee-profiles"
                value={field.value ?? null}
                onChange={field.onChange}
                includeNone
                placeholder="None"
                searchPlaceholder="Search employee profiles…"
                aria-invalid={Boolean(fieldState.error)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        {/* Definition sits on a tooltip, not under the field: a description
            here is a third grid row, which used to leave End date's input 14px
            lower than this one. */}
        <FormField
          control={form.control}
          name="vcStartDate"
          render={({ field }) => (
            <FormItem>
              <FormLabelWithHint hint="Variable Compensation start date.">
                VC Start Date
              </FormLabelWithHint>
              <FormControl>
                <Input type="date" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>End date</FormLabel>
              <FormControl>
                <Input type="date" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Working hours lives in the Employment group here, mirroring the
          profile page where it sits in the Employment card. */}
      <WorkingHoursFields form={form} hasSecondShift={hasSecondShift} />
    </FormSection>
  );
}
